// src/features/whatsapp/components/WhatsAppHealthCard.tsx
// ============================================================================
// SALUD DEL NÚMERO — tarjeta del score 0-100 + semáforo en la página de
// conexión (QR). Lee la foto diaria de /messages/whatsapp/health; responde
// la pregunta del dueño: "¿puedo lanzar campañas hoy?".
// ============================================================================
import React, { useState } from 'react';
import { HeartPulse, MessageCircleReply, MessagesSquare, BellOff, CalendarClock, ShieldCheck, Users, Snowflake, AlertTriangle, Shield, ShieldAlert, HelpCircle } from 'lucide-react';
import type { WaHealth } from '../../../api/whatsappApi';
import SaludTendencia, { MOSTRAR_TENDENCIA_SALUD } from './SaludTendencia';

const fechaCorta = (iso: string) =>
  new Date(`${iso}T12:00:00`).toLocaleDateString('es-MX', { day: '2-digit', month: 'short' });

const SEMAFORO = {
  verde: { label: 'Saludable', chip: 'bg-green-500/15 text-green-600', bar: 'bg-green-500' },
  ambar: { label: 'Precaución', chip: 'bg-amber-500/15 text-amber-600', bar: 'bg-amber-500' },
  rojo:  { label: 'En riesgo', chip: 'bg-red-500/15 text-red-600', bar: 'bg-red-500' },
} as const;

const pct = (r: number | null | undefined) => (r === null || r === undefined) ? null : `${Math.round(r * 100)}%`;

const WhatsAppHealthCard: React.FC<{ health: WaHealth }> = ({ health }) => {
  const [showHelp, setShowHelp] = useState(false);
  const [openTip, setOpenTip] = useState<number | null>(null);
  if (!health.available) {
    return (
      <div className="bg-surface-1 border border-edge rounded-2xl p-5">
        <h2 className="font-semibold text-content-1 flex items-center gap-2">
          <HeartPulse className="w-5 h-5 text-green-500" /> Salud del número
        </h2>
        <p className="text-sm text-content-2 mt-1">
          Aún no hay medición: la primera foto se toma automáticamente mañana por la mañana.
        </p>
      </div>
    );
  }

  const sem = SEMAFORO[health.semaforo || 'rojo'];
  const p = health.parts || {};
  const resp = p['respuesta'];
  const conv = p['conversacion'] as any;      // fotos previas al motor v2
  const bidi = p['bidireccional'];
  const vivos = p['chats_vivos'];
  const carga = p['carga_muda'];
  const frio = p['frio'];
  const mad = p['madurez'];
  const hig = p['higiene'];
  const r463 = p['r463'] as any;
  const tlock = p['timelock'] as any;         // sensor autoritativo (WAHA 2026.8.1); null = API sin dato

  // Orden = importancia real en el score: respuesta (30) > ida y vuelta (20) >
  // el resto (10 c/u). Lo que más pesa se ve primero. `tip` viene del backend
  // (parts.X.explica): la frase con los números reales de ESTE número.
  const rows: Array<{ icon: React.ReactNode; label: string; value: string; tip?: string } | null> = [
    // La restricción vigente va PRIMERO: mientras esté activa, ninguna otra métrica importa.
    tlock && tlock.active ? {
      icon: <ShieldAlert className="w-4 h-4 text-red-500" />, label: 'Restricción de WhatsApp (timelock)',
      value: tlock.hasta ? `activa · hasta el ${fechaCorta(tlock.hasta)}` : 'activa',
      tip: 'WhatsApp está limitando el alcance de este número. No enviar nada proactivo; solo responder a quien escriba.',
    } : null,
    resp && resp.medidos !== undefined && (resp.medidos || 0) > 0 ? {
      icon: <MessageCircleReply className="w-4 h-4" />, label: 'Te responden', tip: resp.explica,
      value: `${resp.respondieron}/${resp.medidos} (${pct(resp.rate) ?? '—'})${resp.via_reaccion ? ` · ${resp.via_reaccion} con reacción` : ''}`,
    } : null,
    bidi && bidi.rate !== null && bidi.rate !== undefined ? {
      icon: <MessagesSquare className="w-4 h-4" />, label: 'Conversación de ida y vuelta', tip: bidi.explica,
      value: `${pct(bidi.rate)} (${bidi.vivos} de ${bidi.contrapartes})`,
    } : null,
    // Fotos viejas (motor v1) sin bidireccional: se muestra su métrica de entonces.
    !bidi && conv && conv.rate !== null && conv.rate !== undefined ? {
      icon: <MessagesSquare className="w-4 h-4" />, label: 'Chats con conversación',
      value: pct(conv.rate) ?? '—',
    } : null,
    vivos ? {
      icon: <Users className="w-4 h-4" />, label: 'Personas que te escriben (90 días)', tip: vivos.explica,
      value: vivos.con_fuente ? `${vivos.vivos}` : 'sin captura aún (sesión WEBJS)',
    } : null,
    // Solo cuenta lo PROACTIVO (campañas/cumpleaños/nurture); bienvenidas,
    // visitas y confirmaciones son transaccionales y no entran aquí. La
    // respuesta solo se mide en envíos con ≥48h de vida ("medidos").
    carga && carga.proactivos_30d !== undefined ? {
      icon: <BellOff className="w-4 h-4" />, label: 'Campañas y felicitaciones (30 días)', tip: carga.explica,
      value: `${carga.proactivos_30d} enviados${carga.sin_respuesta_est !== null && carga.sin_respuesta_est !== undefined && (resp?.medidos || 0) > 0 ? ` · sin respuesta ${carga.sin_respuesta_est} de ${resp!.medidos} medidos` : ' · respuesta aún sin medir'}`,
    } : null,
    frio && (frio.n || 0) >= 1 ? {
      icon: <Snowflake className="w-4 h-4" />, label: 'Envíos en frío (30 días)', tip: frio.explica,
      value: `${frio.frios} de ${frio.n}${frio.nuevos ? ` · ${frio.nuevos} a números nuevos` : ''}`,
    } : null,
    mad && mad.dias !== null && mad.dias !== undefined ? {
      icon: <CalendarClock className="w-4 h-4" />, label: 'Días desde la vinculación', tip: mad.explica,
      value: `${mad.dias}`,
    } : null,
    hig && (hig.envios_14d || 0) >= 10 ? {
      icon: <ShieldCheck className="w-4 h-4" />, label: 'Fallos de entrega (14 días)', tip: hig.explica,
      value: `${hig.fallos_14d} de ${hig.envios_14d}`,
    } : null,
    r463 && r463.dias !== null && r463.dias !== undefined ? {
      icon: <AlertTriangle className="w-4 h-4" />, label: 'Último bloqueo de WhatsApp (463)', tip: r463.explica,
      value: `hace ${r463.dias} días${r463.c30 ? ` · ${r463.c30} en 30 días` : ''}`,
    } : null,
  ];

  return (
    <div className="bg-surface-1 border border-edge rounded-2xl p-5 space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="font-semibold text-content-1 flex items-center gap-2">
          <HeartPulse className="w-5 h-5 text-green-500" /> Salud del número
        </h2>
        <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${sem.chip}`}>{sem.label}</span>
      </div>

      <div className="flex items-center gap-4">
        <div className="text-3xl font-bold text-content-1 tabular-nums shrink-0">
          {health.score}<span className="text-base font-medium text-content-3">/100</span>
        </div>
        <div className="flex-1 h-2.5 rounded-full bg-surface-2 overflow-hidden">
          <div className={`h-full rounded-full ${sem.bar}`} style={{ width: `${health.score || 0}%` }} />
        </div>
      </div>

      <p className="text-sm text-content-2">{health.veredicto}</p>

      {/* Tendencia (22-ago-2026). Va pegada al score porque es el MISMO dato en el
          tiempo: el número solo dice cómo está hoy y esconde de dónde viene — medido,
          8 de 10 negocios cambiaron de color de semáforo en el último mes. El propio
          componente se calla si hay menos de 2 días. */}
      {MOSTRAR_TENDENCIA_SALUD && (health.historia?.puntos?.length ?? 0) >= 2 && (
        <div>
          <p className="text-xs font-semibold text-content-1 mb-1">Cómo ha evolucionado</p>
          <SaludTendencia historia={health.historia} />
        </div>
      )}

      {/* Banner del gate anti-ban (F5b): qué restricción rige y hasta cuándo. */}
      {health.gate?.activo && health.gate.restriccion !== 'ninguna' && (() => {
        const g = health.gate!;
        const todo = g.restriccion === 'todo';
        const cuando = g.hasta ? ` · se libera el ${fechaCorta(g.hasta)}`
          : g.motivo === 'sesion_caida' ? ' · hasta volver a vincular'
          : g.motivo === 'semaforo_rojo' ? ' · hasta que la salud mejore' : '';
        return (
          <div className={`rounded-xl px-3.5 py-2.5 ${todo ? 'bg-red-500/10' : 'bg-amber-500/10'}`}>
            <p className={`text-sm font-medium flex items-center gap-2 ${todo ? 'text-red-600' : 'text-amber-600'}`}>
              {todo ? <ShieldAlert className="w-4 h-4" /> : <Shield className="w-4 h-4" />}
              {todo ? 'Envíos masivos en pausa automática' : 'Protección activa: campañas solo a clientes con relación (conversación o visita reciente)'}
            </p>
            <p className="text-xs mt-0.5 text-content-2">Motivo: {g.label}{cuando}</p>
          </div>
        );
      })()}
      {(health.gate?.rechazos?.length ?? 0) > 0 && (
        <p className="text-xs text-content-3 flex items-center gap-1.5">
          <Shield className="w-3 h-3" />
          Protegido esta semana: {health.gate!.rechazos!.reduce((a, r) => a + r.n, 0)} envíos frenados
          {' '}({health.gate!.rechazos!.map((r) => `${r.n} ${r.label}`).join(' · ')})
        </p>
      )}

      {rows.some(Boolean) && (
        <div className="grid sm:grid-cols-2 gap-x-6 gap-y-2 pt-1">
          {rows.filter(Boolean).map((r, i) => (
            <div key={i}>
              <button
                type="button"
                onClick={() => r!.tip && setOpenTip(openTip === i ? null : i)}
                title={r!.tip}
                className="w-full flex items-center justify-between gap-2 text-sm text-left"
              >
                <span className="flex items-center gap-2 text-content-2">{r!.icon} {r!.label}</span>
                <span className="font-medium text-content-1 text-right">{r!.value}</span>
              </button>
              {openTip === i && r!.tip && (
                <p className="text-xs text-content-3 mt-1 pl-6">{r!.tip}</p>
              )}
            </div>
          ))}
        </div>
      )}
      {rows.some((r) => r?.tip) && (
        <p className="text-[11px] text-content-3 -mt-1">Toca cualquier dato para ver qué significa con tus números.</p>
      )}

      <button
        onClick={() => setShowHelp(!showHelp)}
        className="text-xs font-medium text-content-2 flex items-center gap-1.5"
      >
        <HelpCircle className="w-3.5 h-3.5" /> ¿Cómo se calcula el score?
      </button>
      {showHelp && (
        <div className="rounded-xl bg-surface-2 px-3.5 py-2.5 text-xs text-content-2 space-y-1.5">
          <p>
            Mide qué tan «sano» ve WhatsApp a tu número. Lo que <span className="font-medium text-content-1">más pesa</span> es
            la conversación real: que la gente <span className="font-medium text-content-1">te responda</span> (30 pts) y que
            el chat sea de <span className="font-medium text-content-1">ida y vuelta</span> (20 pts). El resto: personas que te
            escriben, mensajes sin respuesta, envíos a desconocidos, antigüedad de la vinculación y fallos de entrega (10 pts cada uno).
          </p>
          <p>
            Solo se promedia lo que ya tiene datos suficientes — lo que aún no se puede medir
            <span className="font-medium text-content-1"> no cuenta ni castiga</span>.
          </p>
          <p>
            <span className="font-medium text-green-600">Verde (70+)</span>: puedes lanzar campañas ·{' '}
            <span className="font-medium text-amber-600">Ámbar (40-69)</span>: solo clientes con conversación reciente ·{' '}
            <span className="font-medium text-red-600">Rojo (&lt;40)</span>: deja descansar el número.
          </p>
        </div>
      )}

      <p className="text-[11px] text-content-3">
        Medido el {new Date(health.measured_at || '').toLocaleDateString('es-MX', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
        {' '}· se recalcula cada mañana{health.stale ? ' · ⚠️ medición atrasada' : ''}
      </p>
    </div>
  );
};

export default WhatsAppHealthCard;
